import React, { useMemo } from "react";
import * as THREE from "three";


/*
 * ============================================================
 * Plant
 * ============================================================
 *
 * Procedural plant model.
 *
 * Stage 1:
 * - Stem
 * - Leaves
 * - Roots
 * - Growth medium
 *
 * Later:
 * - height comes from biological simulator
 * - leaf count comes from simulator
 * - health changes plant appearance
 * - growth animation
 */


function Leaf({
    position,
    rotation,
    scale = 1,
}) {

    const geometry = useMemo(() => {

        const shape = new THREE.Shape();

        /*
         * Leaf shape
         *
         *             tip
         *              ▲
         *             / \
         *            /   \
         *           /     \
         *          /       \
         *         ─────────
         */

        shape.moveTo(0, 0);

        shape.bezierCurveTo(
            0.25,
            0.15,
            0.65,
            0.20,
            1.0,
            0.0
        );

        shape.bezierCurveTo(
            0.65,
            -0.20,
            0.25,
            -0.15,
            0,
            0
        );

        return new THREE.ShapeGeometry(shape);

    }, []);


    return (
        <mesh
            position={position}
            rotation={rotation}
            scale={scale}
            geometry={geometry}
        >

            <meshStandardMaterial
                color="#3fa66b"
                side={THREE.DoubleSide}
                roughness={0.8}
            />

        </mesh>
    );
}


function Root({
    rotation,
    length = 1,
}) {

    return (
        <mesh
            rotation={rotation}
            position={[
                0,
                -0.25,
                0,
            ]}
            scale={[
                0.06,
                length,
                0.06,
            ]}
        >

            <cylinderGeometry
                args={[
                    0.5,
                    0.25,
                    1,
                    8,
                ]}
            />

            <meshStandardMaterial
                color="#c29b72"
                roughness={1}
            />

        </mesh>
    );
}


function Stem({
    height = 4,
}) {

    return (
        <mesh
            position={[
                0,
                height / 2,
                0,
            ]}
        >

            <cylinderGeometry
                args={[
                    0.10,
                    0.15,
                    height,
                    12,
                ]}
            />

            <meshStandardMaterial
                color="#4b9b62"
                roughness={0.85}
            />

        </mesh>
    );
}


function GrowthMedium() {

    return (
        <mesh
            position={[
                0,
                -0.5,
                0,
            ]}
        >

            <cylinderGeometry
                args={[
                    1.5,
                    1.5,
                    0.5,
                    32,
                ]}
            />

            <meshStandardMaterial
                color="#4d3829"
                roughness={1}
            />

        </mesh>
    );
}


/*
 * ============================================================
 * Main Plant
 * ============================================================
 */

export default function Plant({
    height = 4,
    leafCount = 8,
    leafSize = 1,
    health = 100,
}) {

    /*
     * Health will eventually influence the plant.
     *
     * For Stage 1 we only use it to slightly modify
     * the leaf appearance.
     */

    const leafColor = useMemo(() => {

        if (health > 80) {
            return "#3fa66b";
        }

        if (health > 50) {
            return "#789c45";
        }

        return "#8a7040";

    }, [health]);


    /*
     * Generate leaf positions.
     */

    const leaves = useMemo(() => {

        const result = [];

        const verticalSpacing =
            height / (leafCount + 1);


        for (let i = 0; i < leafCount; i++) {

            const y =
                verticalSpacing * (i + 1);


            /*
             * Spiral leaves around the stem.
             *
             * Golden-angle approximation.
             */

            const angle =
                i * 2.39996;


            const radius =
                0.10 + (i / leafCount) * 0.05;


            const x =
                Math.cos(angle) * radius;

            const z =
                Math.sin(angle) * radius;


            /*
             * Alternate leaf direction.
             */

            const direction =
                i % 2 === 0
                    ? 1
                    : -1;


            result.push({
                position: [
                    x,
                    y,
                    z,
                ],

                rotation: [
                    0,
                    angle,
                    direction * 0.25,
                ],

                scale:
                    leafSize *
                    (
                        0.55 +
                        (i / leafCount) * 0.45
                    ),
            });

        }

        return result;

    }, [
        height,
        leafCount,
        leafSize,
    ]);


    /*
     * Roots
     */

    const roots = useMemo(() => {

        return Array.from(
            {
                length: 8,
            },
            (_, i) => {

                const angle =
                    (i / 8) *
                    Math.PI *
                    2;

                return {
                    rotation: [
                        0.65,
                        angle,
                        0,
                    ],
                    length:
                        0.7 +
                        Math.random() * 0.4,
                };
            }
        );

    }, []);


    return (
        <group>

            {/* Growth medium */}

            <GrowthMedium />


            {/* Stem */}

            <Stem
                height={height}
            />


            {/* Leaves */}

            {leaves.map(
                (leaf, index) => (

                    <Leaf
                        key={index}
                        position={
                            leaf.position
                        }
                        rotation={
                            leaf.rotation
                        }
                        scale={
                            leaf.scale
                        }
                    />

                )
            )}


            {/* Roots */}

            {roots.map(
                (root, index) => (

                    <Root
                        key={index}
                        rotation={
                            root.rotation
                        }
                        length={
                            root.length
                        }
                    />

                )
            )}

        </group>
    );
}